import {useSelector} from '@xstate/react';
import React, {useMemo} from 'react';
import {ActorRefFrom} from 'xstate';

import ParametersForPlateMapWrapper from 'notebook/integrations/procedures/ParametersForPlateMapWrapper';
import {PlateDataParameterContext} from 'samples/plate-maps/hooks/usePlateDataParameterContext';
import {ParameterFieldType, PlateDataParameter} from 'samples/plate-maps/PlateMapConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

import DefineScratchPlateMapStep, {DefineScratchPlateMapStepProps} from './DefineScratchPlateMapStep';
import ScratchPlateConcentrationLayerProvider from './ScratchPlateConcentrationLayerProvider';
import ScratchPlateMetadataLayerProvider from './ScratchPlateMetadataLayerProvider';
import {ScratchPlateMachine} from './ScratchPlateModalMachine';
import ScratchPlateParameterLayerProvider from './ScratchPlateParameterLayerProvider';

export interface ConnectedDefineScratchPlateMapStepProps {
  plateActor: ActorRefFrom<typeof ScratchPlateMachine>;
  wellSchemaLayers: DefineScratchPlateMapStepProps['wellSchemaLayers'];
  maxPlateViewHeight: string;
  /**
   * Called when an edit invalidates previously-resolved transfer contents — i.e. plate-map or
   * concentration-layer changes. Metadata-layer edits intentionally do not invalidate contents
   * since they don't affect fill/transfer results.
   */
  onInvalidateContents?: () => void;
}

/** Maps a procedure data parameter's schema field type to the scratch-plate parameter field type. */
export function dataParameterFieldType(parameter: PlateDataParameter): ParameterFieldType {
  switch (parameter.schemaField.fieldType) {
    case FieldType.FLOAT:
      return ParameterFieldType.FLOAT;
    case FieldType.DATETIME:
      return ParameterFieldType.DATETIME;
    default:
      return ParameterFieldType.TEXT;
  }
}

/**
 * Wires {@link DefineScratchPlateMapStep} to a {@link ScratchPlateMachine} actor: it sources plate
 * state from the actor, provides the concentration/metadata/parameter layer actors via context, and
 * forwards each layer/schema-field action as the corresponding machine event. This keeps the
 * create-records and design modals from duplicating the (verbose) wiring.
 *
 * Procedure association: this wraps the step in {@link ParametersForPlateMapWrapper}, which resolves
 * the worksheet/procedure-step context (via WorksheetContext) and supplies the procedure's
 * plate-map data parameters. Outside a procedure it gracefully yields no parameters, so the
 * Parameters section is simply empty. This mirrors how the legacy plate map associates with a
 * procedure — no persisted FK; the association is the edit-time worksheet context.
 */
export default function ConnectedDefineScratchPlateMapStep(
  props: ConnectedDefineScratchPlateMapStepProps
): JSX.Element {
  return (
    <ParametersForPlateMapWrapper>
      {({plateDataParameters = []}) => (
        <ConnectedDefineScratchPlateMapStepInner {...props} plateDataParameters={plateDataParameters} />
      )}
    </ParametersForPlateMapWrapper>
  );
}

function ConnectedDefineScratchPlateMapStepInner({
  plateActor,
  wellSchemaLayers,
  maxPlateViewHeight,
  onInvalidateContents,
  plateDataParameters,
}: ConnectedDefineScratchPlateMapStepProps & {
  plateDataParameters: Array<PlateDataParameter>;
}): JSX.Element {
  const plateState = useSelector(plateActor, (snap) => snap.context);
  const invalidateContents = (): void => onInvalidateContents?.();
  const plateDataParameterContextValue = useMemo(() => ({plateDataParameters}), [plateDataParameters]);

  return (
    <PlateDataParameterContext.Provider value={plateDataParameterContextValue}>
      <ScratchPlateConcentrationLayerProvider actor={plateState.concentrationLayerMachine!}>
        <ScratchPlateMetadataLayerProvider actor={plateState.metadataLayerMachine!}>
          <ScratchPlateParameterLayerProvider actor={plateState.parameterLayerMachine!}>
            {plateState.plateMap != null && (
              <DefineScratchPlateMapStep
                plateMap={plateState.plateMap}
                setPlateMap={(plateMap) => {
                  plateActor.send({type: 'updatePlateMap', plateMap});
                  invalidateContents();
                }}
                containerSchemaId={plateState.plateSchema?.containerSchemaId ?? null}
                wellSchemaLayers={wellSchemaLayers}
                selectedWellSchemaFieldIds={plateState.selectedWellSchemaFieldIds}
                plateDataParameters={plateDataParameters}
                onAddSelectedSchemaField={(schemaFieldId) => {
                  plateActor.send({type: 'addSelectedSchemaFieldId', schemaFieldId});
                }}
                onRemoveSelectedSchemaField={(schemaFieldId) => {
                  plateActor.send({type: 'removeSelectedSchemaFieldId', schemaFieldId});
                }}
                onAddConcentrationLayer={() => {
                  plateActor.send({type: 'addConcentrationLayer'});
                  invalidateContents();
                }}
                onRemoveConcentrationLayer={(layerId) => {
                  plateActor.send({type: 'removeConcentrationLayer', layerId});
                  invalidateContents();
                }}
                onAddMetadataLayer={() => {
                  plateActor.send({type: 'addMetadataLayer'});
                }}
                onRemoveMetadataLayer={(layerId) => {
                  plateActor.send({type: 'removeMetadataLayer', layerId});
                }}
                onUpdateMetadataLayerType={(layerId, layerType) => {
                  plateActor.send({type: 'updateMetadataLayerType', layerId, layerType});
                }}
                onAddParameterLayer={(parameter) => {
                  plateActor.send({
                    type: 'addParameterLayer',
                    parameterVersionId: parameter.versionId,
                    name: parameter.name,
                    fieldType: dataParameterFieldType(parameter),
                  });
                }}
                onRemoveParameterLayer={(layerId) => {
                  plateActor.send({type: 'removeParameterLayer', layerId});
                }}
                maxPlateViewHeight={maxPlateViewHeight}
              />
            )}
          </ScratchPlateParameterLayerProvider>
        </ScratchPlateMetadataLayerProvider>
      </ScratchPlateConcentrationLayerProvider>
    </PlateDataParameterContext.Provider>
  );
}
